/**
 * Compras — Revisar a nota antes de importar.
 * Rota `/app/compras/notas-fiscais/importar/revisar`.
 *
 * Esta é a última tela antes de o sistema mexer em cadastro de fornecedor, em
 * documento fiscal e em saldo de estoque. Nada foi gravado quando ela abre, e
 * tudo é gravado quando ela fecha pelo botão. O desenho tem que dizer isso —
 * daí o selo "ainda não importada" no topo e o resumo do que vai acontecer em
 * cima do único botão que grava.
 *
 * Duas coisas estruturam a tela:
 *
 * 1. O DOCUMENTO é uma folha só: uma borda, divisores por dentro. Fornecedor,
 *    itens, totais e autorização são partes da MESMA nota — cartões flutuando
 *    sugeririam assuntos independentes.
 *
 * 2. As DECISÕES ficam fora da folha, num cartão próprio. Acima está o que o
 *    fornecedor declarou; aqui está o que VOCÊ escolhe. Misturar os dois faria
 *    parecer que a chave "lançar no estoque" veio no XML.
 *
 * A conciliação acontece AQUI, antes de gravar: o item que o EAN não resolveu
 * espera um código de barras nesta tela, e a nota entra inteira ou não entra.
 * Conciliar depois continua possível pela ficha — mas quem faz aqui não deixa
 * item pendente para achar na semana que vem.
 *
 * A tela só existe com a prévia que a importação passou pelo state da rota.
 * Sem ela não há o que revisar — volta para o upload, em vez de desenhar
 * "dados não encontrados", que não é lugar nenhum.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { Chave } from '../../componentes/Campos'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { mascararCEP, mascararDocumento } from '../../plataforma/mascaras'
import {
  importarNotaFiscal,
  type ConciliacaoEscolhida,
  type ItemPrevia,
  type PreviaNota,
  type SKUResumo,
} from '../../plataforma/notasfiscais'
import { LayoutApp } from '../app/LayoutApp'
import { BuscaDeSKU } from '../produtos/BuscaDeSKU'

/** O que a tela de importação passa pela rota. */
export type EstadoDaRevisao = {
  previa: PreviaNota
  /** O XML cru, que volta ao servidor na conclusão — ver `PedidoDeImportacao`. */
  xml: string
  nomeArquivo: string
}

const CAMINHO_IMPORTAR = '/app/compras/notas-fiscais/importar'
const CAMINHO_LISTA = '/app/compras/notas-fiscais'

export function TelaRevisarNotaFiscal() {
  const navegar = useNavigate()
  const local = useLocation()
  const estado = local.state as EstadoDaRevisao | null

  const [escolhas, setEscolhas] = useState<Record<number, string>>({})
  const [editando, setEditando] = useState<Record<number, boolean>>({})
  const [lancarEstoque, setLancarEstoque] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState('')
  const [errosPorItem, setErrosPorItem] = useState<Record<number, string>>({})

  useEffect(() => {
    if (!estado?.previa) navegar(CAMINHO_IMPORTAR, { replace: true })
  }, [estado, navegar])

  const previa = estado?.previa

  /** O SKU que vale para o item agora: o digitado vence o sugerido pelo EAN. */
  const decisaoDoItem = useMemo(() => {
    return (item: ItemPrevia): { codigo: string; sku?: SKUResumo; origem: 'ean' | 'escolhido' | 'nenhuma' } => {
      const digitado = (escolhas[item.numero_item] ?? '').trim()
      if (digitado) return { codigo: digitado, origem: 'escolhido' }
      if (item.sku_sugerido) return { codigo: item.sku_sugerido.codigo_barras, sku: item.sku_sugerido, origem: 'ean' }
      return { codigo: '', origem: 'nenhuma' }
    }
  }, [escolhas])

  const pendentes = useMemo(
    () => (previa?.itens ?? []).filter((item) => decisaoDoItem(item).origem === 'nenhuma'),
    [previa, decisaoDoItem],
  )

  if (!previa) return null

  const conciliados = previa.itens.length - pendentes.length
  const somaDosItens = previa.itens.reduce((soma, item) => soma + item.valor_total_centavos, 0)

  async function concluir() {
    if (!previa || !estado) return

    setErro('')
    setErrosPorItem({})
    setEnviando(true)

    // Só as conciliações DIGITADAS viajam: o casamento por EAN o servidor
    // refaz sozinho, e reenviá-lo seria repetir uma decisão que não é nossa.
    const conciliacoes: ConciliacaoEscolhida[] = Object.entries(escolhas)
      .map(([numero, codigo]) => ({ numero_item: Number(numero), sku: codigo.trim() }))
      .filter((escolha) => escolha.sku !== '')

    try {
      const resultado = await importarNotaFiscal({
        xml: estado.xml,
        conciliacoes,
        lancar_estoque: lancarEstoque,
      })

      navegar(CAMINHO_LISTA, {
        replace: true,
        state: {
          chaveImportada: resultado.nota.chave_acesso,
          avisoImportacao: mensagemDoResultado(
            resultado.nota.numero,
            resultado.estoque_lancado,
            resultado.itens_lancados.length,
            resultado.nota.itens_pendentes,
          ),
        },
      })
    } catch (causa: unknown) {
      if (causa instanceof ErroApi) {
        setErro(causa.message)
        // O 422 aponta o item pelo campo `item_N` — a mensagem sozinha não
        // diria em qual das trinta linhas está o código errado.
        const porItem: Record<number, string> = {}
        for (const [campo, mensagem] of Object.entries(causa.campos)) {
          const numero = Number(campo.replace('item_', ''))
          if (campo.startsWith('item_') && Number.isFinite(numero)) porItem[numero] = mensagem
        }
        setErrosPorItem(porItem)
      } else {
        setErro('Não foi possível importar a nota. Nada foi gravado.')
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <LayoutApp
      trilha={[
        { rotulo: 'Compras', para: CAMINHO_LISTA },
        { rotulo: 'Notas fiscais', para: CAMINHO_LISTA },
        { rotulo: 'Importar', para: CAMINHO_IMPORTAR },
        { rotulo: 'Revisar' },
      ]}
      voltarPara={CAMINHO_IMPORTAR}
      titulo={`Revisar a nota ${previa.numero || 's/nº'}`}
      subtitulo="Confira os itens antes de gravar — nada entrou no estoque ainda"
      acoes={
        <LinkBotao para={CAMINHO_IMPORTAR} variante="secundario" icone="documento_importar">
          Trocar de arquivo
        </LinkBotao>
      }
    >
      <div className="flex w-full flex-col gap-4 pb-4">
        {erro && (
          <div role="alert" className="flex items-start gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            <Icone nome="error" tamanho={18} className="mt-px shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        {previa.ja_importada && <FaixaJaImportada previa={previa} />}

        {/* ── a folha: uma borda, divisores por dentro ── */}
        <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
          <div className="flex flex-wrap items-start justify-between gap-4 p-5">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="font-titulo text-[17px] font-semibold text-tinta">
                  NF-e {previa.numero || 's/nº'}
                  <span className="tabular text-texto-suave">/{previa.serie || '—'}</span>
                </h2>
                <span className="inline-flex items-center rounded-[6px] border border-borda bg-superficie px-2.5 py-1 text-xs font-semibold text-texto-suave">
                  Ainda não importada
                </span>
              </div>
              <p className="mt-1 text-[13.5px] text-texto">
                {previa.emitente.nome} ·{' '}
                <span className="tabular font-semibold text-tinta">
                  {formatarMoeda(previa.valor_total_centavos)}
                </span>{' '}
                · emitida em{' '}
                <span className="tabular">{new Date(previa.emitida_em).toLocaleDateString('pt-BR')}</span>
              </p>
              <p className="mt-1.5 max-w-[84ch] text-[12.5px] leading-[1.5] text-texto-suave">
                Nada foi gravado ainda. Confira os itens, resolva o que ficou sem SKU e decida o que a
                importação deve fazer — a conclusão está no fim da página.
              </p>
            </div>

            {estado?.nomeArquivo && (
              <div className="rounded-sistema border border-borda bg-fundo px-3 py-2 text-right">
                <div className="text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">Arquivo</div>
                <div className="tabular mt-0.5 max-w-[240px] truncate text-[12.5px] text-texto">
                  {estado.nomeArquivo}
                </div>
              </div>
            )}
          </div>

          <Secao titulo="Fornecedor">
            <div className="grid grid-cols-2 gap-x-4 gap-y-4 md:grid-cols-4">
              <Campo rotulo="Nome" larga>
                {previa.emitente.nome}
              </Campo>
              <Campo rotulo={previa.emitente.eh_pessoa_juridica ? 'CNPJ' : 'CPF'} mono>
                {mascararDocumento(
                  previa.emitente.eh_pessoa_juridica ? 'pj' : 'pf',
                  previa.emitente.documento,
                )}
              </Campo>
              <Campo rotulo="Inscrição estadual" mono>
                {previa.emitente.inscricao_estadual}
              </Campo>
              <Campo rotulo="Natureza da operação" larga>
                {previa.natureza_operacao}
              </Campo>
              <Campo rotulo="Endereço" larga>
                {enderecoEmLinha(previa)}
              </Campo>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              {previa.fornecedor.cadastrado ? (
                <>
                  <span className="inline-flex items-center gap-1.5 rounded-[6px] border border-borda bg-divisoria px-2.5 py-1 text-xs font-semibold text-texto-suave">
                    <Icone nome="check" tamanho={13} />
                    Já cadastrado
                  </span>
                  <span className="tabular text-[12.5px] text-texto-suave">
                    {previa.fornecedor.codigo} · {previa.fornecedor.nome}
                  </span>
                </>
              ) : (
                <>
                  {/* Sem cor: não há providência a tomar. O cadastro sai do
                      documento assinado pela nota, e a importação o cria
                      sozinha — âmbar aqui pediria uma ação que não existe. */}
                  <span className="inline-flex items-center rounded-[6px] border border-borda bg-superficie px-2.5 py-1 text-xs font-semibold text-texto-suave">
                    Sem cadastro
                  </span>
                  <span className="text-[12.5px] text-texto-suave">
                    Vai ser criado ao importar, com os dados do emitente.
                  </span>
                </>
              )}
            </div>
          </Secao>

          <Secao
            titulo="Itens da nota"
            apoio={
              pendentes.length > 0
                ? 'Item sem SKU não entra no estoque. Bipe ou digite o código de barras da peça correspondente no seu catálogo.'
                : 'Todo item desta nota tem um SKU correspondente no seu catálogo.'
            }
            extra={
              <span className="tabular text-[12.5px] text-texto-suave">
                {previa.itens.length} {previa.itens.length === 1 ? 'item' : 'itens'} ·{' '}
                {formatarMoeda(somaDosItens)}
              </span>
            }
          >
            <div className="overflow-x-auto rounded-sistema border border-borda">
              <table className="w-full min-w-[900px] border-collapse text-left">
                <caption className="sr-only">Itens da nota fiscal a importar</caption>
                <thead>
                  <tr>
                    <th scope="col" className={`${thCls} w-[46px]`}>
                      #
                    </th>
                    <th scope="col" className={thCls}>
                      Descrição
                    </th>
                    <th scope="col" className={`${thCls} w-[86px] text-right`}>
                      Qtd.
                    </th>
                    <th scope="col" className={`${thCls} w-[110px] text-right`}>
                      Unitário
                    </th>
                    <th scope="col" className={`${thCls} w-[110px] text-right`}>
                      Total
                    </th>
                    <th scope="col" className={`${thCls} w-[300px]`}>
                      SKU da loja
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previa.itens.map((item) => (
                    <LinhaDeItem
                      key={item.numero_item}
                      item={item}
                      decisao={decisaoDoItem(item)}
                      valorDigitado={escolhas[item.numero_item] ?? ''}
                      erro={errosPorItem[item.numero_item]}
                      editando={!!editando[item.numero_item]}
                      aoEditar={(ligado) =>
                        setEditando((atual) => ({ ...atual, [item.numero_item]: ligado }))
                      }
                      aoEscolher={(codigo) =>
                        setEscolhas((atual) => ({ ...atual, [item.numero_item]: codigo }))
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Secao>

          <Secao
            titulo="Totais da nota"
            apoio="Os valores vêm do XML e não são recalculados — é o que o fornecedor declarou à SEFAZ."
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="grid grid-cols-2 gap-x-4 gap-y-4 md:grid-cols-3">
                <Campo rotulo="Produtos" mono>
                  {formatarMoeda(previa.valor_produtos_centavos)}
                </Campo>
                <Campo rotulo="IBS" mono>
                  {formatarMoeda(previa.valor_ibs_centavos)}
                </Campo>
                <Campo rotulo="CBS" mono>
                  {formatarMoeda(previa.valor_cbs_centavos)}
                </Campo>
                {/* Bloco clássico (ICMSTot) — convive com IBS/CBS durante a
                    transição da reforma tributária. Só aparece quando a nota
                    de fato traz o valor: campo zerado por padrão não é dado,
                    é ausência, e mostrar oito campos zerados em toda nota
                    pela regra nova seria ruído. */}
                {!!previa.valor_icms_centavos && (
                  <Campo rotulo="ICMS" mono>
                    {formatarMoeda(previa.valor_icms_centavos)}
                  </Campo>
                )}
                {!!previa.valor_ipi_centavos && (
                  <Campo rotulo="IPI" mono>
                    {formatarMoeda(previa.valor_ipi_centavos)}
                  </Campo>
                )}
                {!!previa.valor_pis_centavos && (
                  <Campo rotulo="PIS" mono>
                    {formatarMoeda(previa.valor_pis_centavos)}
                  </Campo>
                )}
                {!!previa.valor_cofins_centavos && (
                  <Campo rotulo="COFINS" mono>
                    {formatarMoeda(previa.valor_cofins_centavos)}
                  </Campo>
                )}
                {!!previa.valor_frete_centavos && (
                  <Campo rotulo="Frete" mono>
                    {formatarMoeda(previa.valor_frete_centavos)}
                  </Campo>
                )}
                {!!previa.valor_seguro_centavos && (
                  <Campo rotulo="Seguro" mono>
                    {formatarMoeda(previa.valor_seguro_centavos)}
                  </Campo>
                )}
                {!!previa.valor_outras_despesas_centavos && (
                  <Campo rotulo="Outras despesas" mono>
                    {formatarMoeda(previa.valor_outras_despesas_centavos)}
                  </Campo>
                )}
                {!!previa.valor_desconto_centavos && (
                  <Campo rotulo="Desconto" mono>
                    − {formatarMoeda(previa.valor_desconto_centavos)}
                  </Campo>
                )}
              </div>

              <div className="rounded-sistema border border-borda bg-fundo p-3.5 lg:self-start">
                <LinhaTotal rotulo="Produtos" valor={previa.valor_produtos_centavos} />
                <LinhaTotal rotulo="IBS + CBS" valor={previa.valor_ibs_centavos + previa.valor_cbs_centavos} />
                {!!(
                  previa.valor_icms_centavos ||
                  previa.valor_ipi_centavos ||
                  previa.valor_pis_centavos ||
                  previa.valor_cofins_centavos
                ) && (
                  <LinhaTotal
                    rotulo="ICMS + IPI + PIS + COFINS"
                    valor={
                      (previa.valor_icms_centavos ?? 0) +
                      (previa.valor_ipi_centavos ?? 0) +
                      (previa.valor_pis_centavos ?? 0) +
                      (previa.valor_cofins_centavos ?? 0)
                    }
                  />
                )}
                {!!(previa.valor_frete_centavos || previa.valor_seguro_centavos || previa.valor_outras_despesas_centavos) && (
                  <LinhaTotal
                    rotulo="Frete + seguro + outras despesas"
                    valor={
                      (previa.valor_frete_centavos ?? 0) +
                      (previa.valor_seguro_centavos ?? 0) +
                      (previa.valor_outras_despesas_centavos ?? 0)
                    }
                  />
                )}
                {!!previa.valor_desconto_centavos && (
                  <LinhaTotal rotulo="Desconto" valor={-previa.valor_desconto_centavos} />
                )}
                <div className="mt-2 border-t border-borda pt-2">
                  <LinhaTotal rotulo="Total da nota" valor={previa.valor_total_centavos} forte />
                </div>
              </div>
            </div>
          </Secao>

          <Secao titulo="Autorização na SEFAZ">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_200px]">
              <Campo rotulo="Chave de acesso" mono>
                {chaveEspacada(previa.chave_acesso)}
              </Campo>
              <Campo rotulo="Emissão" mono>
                {new Date(previa.emitida_em).toLocaleString('pt-BR')}
              </Campo>
            </div>
          </Secao>
        </div>

        {/* ══ as decisões ══ */}
        <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
          <div className="border-b border-divisoria px-5 py-3.5">
            <h3 className="font-titulo text-[15px] font-semibold text-tinta">
              O que a importação vai fazer
            </h3>
            <p className="mt-0.5 max-w-[84ch] text-[12.5px] leading-[1.5] text-texto-suave">
              A nota é gravada de qualquer forma, com todos os itens. O que a chave abaixo decide é o que
              acontece <span className="font-semibold text-texto">fora</span> dela — no saldo do estoque.
            </p>
          </div>

          <div className="flex flex-col gap-4 p-5">
            <Chave
              ligada={lancarEstoque}
              aoMudar={setLancarEstoque}
              rotulo="Dar entrada no estoque agora"
              ajuda={
                lancarEstoque ? (
                  <>
                    Soma a quantidade de cada item conciliado ao saldo da unidade{' '}
                    <span className="font-semibold text-texto">{previa.filial_destino.nome}</span>, com o
                    custo desta nota. Item sem SKU não entra e continua esperando na ficha.
                  </>
                ) : (
                  'A nota fica gravada e os itens conciliados, mas nenhum saldo muda. Você lança depois, pela ficha da nota.'
                )
              }
            />

            {/* Impedir vale mais que avisar: a razão fica escrita, não num
                alerta depois do clique. */}
            {lancarEstoque && pendentes.length > 0 && (
              <p className="flex items-start gap-2 rounded-sistema bg-atencao-suave px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-atencao-texto">
                <Icone nome="warning" tamanho={16} className="mt-px shrink-0" />
                <span>
                  {pendentes.length} {pendentes.length === 1 ? 'item ficará de fora' : 'itens ficarão de fora'}{' '}
                  da entrada — {pendentes.length === 1 ? 'ele' : 'eles'} ainda não têm SKU. Conciliar agora
                  evita procurar essa pendência depois.
                </span>
              </p>
            )}
          </div>

          {/* O resumo fica em cima do botão que grava, e não no topo da página
              — depois de rolar a nota inteira, ninguém volta ao começo para
              conferir o que vai acontecer. */}
          <div className="border-t border-divisoria bg-fundo p-5">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">
                  Ao concluir
                </p>
                <ul className="mt-2 flex list-none flex-col gap-1 p-0 text-[13px] leading-[1.5] text-texto">
                  <li>
                    · Grava a nota {previa.numero || 's/nº'} com{' '}
                    <span className="tabular">{previa.itens.length}</span>{' '}
                    {previa.itens.length === 1 ? 'item' : 'itens'}, no valor de{' '}
                    <span className="tabular">{formatarMoeda(previa.valor_total_centavos)}</span>
                  </li>
                  <li>
                    ·{' '}
                    {previa.fornecedor.cadastrado
                      ? `Vincula ao fornecedor ${previa.fornecedor.codigo} — ${previa.fornecedor.nome}`
                      : `Cadastra ${previa.emitente.nome} como fornecedor novo`}
                  </li>
                  <li>
                    ·{' '}
                    {lancarEstoque
                      ? `Dá entrada de ${conciliados} ${conciliados === 1 ? 'item' : 'itens'} em ${previa.filial_destino.nome}`
                      : 'Não mexe no estoque'}
                  </li>
                  {pendentes.length > 0 && (
                    <li>
                      · Deixa <span className="tabular">{pendentes.length}</span>{' '}
                      {pendentes.length === 1 ? 'item pendente' : 'itens pendentes'} de conciliação
                    </li>
                  )}
                </ul>
              </div>

              <div className="flex flex-none flex-wrap items-center gap-2.5">
                <LinkBotao para={CAMINHO_LISTA} variante="secundario">
                    Cancelar
                  </LinkBotao>
                <Botao
                    icone="check"
                    disabled={enviando || previa.ja_importada || previa.itens.length === 0}
                    onClick={() => void concluir()}
                  >
                    {enviando ? 'Importando…' : 'Importar nota'}
                  </Botao>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ peças */

const thCls =
  'bg-fundo px-3 py-2.5 text-left text-[11px] font-bold tracking-[0.6px] uppercase text-texto-suave whitespace-nowrap'
const tdCls = 'border-t border-divisoria px-3 py-2.5 align-top text-[13px] text-texto'

function Secao({
  titulo,
  apoio,
  extra,
  children,
}: {
  titulo: string
  apoio?: string
  extra?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="border-t border-divisoria p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[13.5px] font-bold text-tinta">{titulo}</h3>
          {apoio && <p className="mt-0.5 max-w-[84ch] text-[12.5px] leading-[1.5] text-texto-suave">{apoio}</p>}
        </div>
        {extra}
      </div>
      {children}
    </div>
  )
}

/** Rótulo pequeno + valor. Vazio é "—", nunca um placeholder inventado. */
function Campo({
  rotulo,
  children,
  mono,
  larga,
}: {
  rotulo: string
  children?: React.ReactNode
  mono?: boolean
  larga?: boolean
}) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${larga ? 'md:col-span-2' : ''}`}>
      <span className="text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">{rotulo}</span>
      <span className={`break-words text-[13.5px] text-tinta ${mono ? 'tabular' : ''}`}>{children || '—'}</span>
    </div>
  )
}

function LinhaTotal({ rotulo, valor, forte }: { rotulo: string; valor: number; forte?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-[3px] text-[13px]">
      <span className={forte ? 'font-semibold text-tinta' : 'text-texto-suave'}>{rotulo}</span>
      <span className={`tabular ${forte ? 'font-semibold text-tinta' : 'text-tinta'}`}>
        {formatarMoeda(valor)}
      </span>
    </div>
  )
}

function FaixaJaImportada({ previa }: { previa: PreviaNota }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-sistema border border-marca-borda bg-marca-fundo-2 px-4 py-3 text-[13px] text-marca-forte">
      <Icone nome="info" tamanho={18} className="shrink-0" />
      <span className="min-w-0 flex-1">
        Esta nota já foi importada
        {previa.importada_em ? ` em ${new Date(previa.importada_em).toLocaleString('pt-BR')}` : ''}. Importar de
        novo criaria um segundo documento e uma segunda entrada de estoque — por isso a conclusão está
        bloqueada.
      </span>
      <Link
        to={CAMINHO_LISTA}
        className="h-8 shrink-0 rounded-sistema border border-marca-borda bg-superficie px-3 text-[12.5px] leading-8 font-semibold text-marca-forte"
      >
        Ver as notas importadas
      </Link>
    </div>
  )
}

function LinhaDeItem({
  item,
  decisao,
  valorDigitado,
  erro,
  editando,
  aoEditar,
  aoEscolher,
}: {
  item: ItemPrevia
  decisao: { codigo: string; sku?: SKUResumo; origem: 'ean' | 'escolhido' | 'nenhuma' }
  /** O que está no campo. Vem do estado da tela, nunca da decisão calculada:
   *  um input cujo valor é derivado do próprio resultado não deixa apagar. */
  valorDigitado: string
  erro?: string
  editando: boolean
  aoEditar: (ligado: boolean) => void
  aoEscolher: (codigo: string) => void
}) {
  /*
   * Quem manda no campo é a ORIGEM DA SUGESTÃO, não a decisão atual.
   *
   * Com `decisao.origem === 'nenhuma'` aqui, digitar a primeira tecla fazia a
   * origem virar "escolhido", o campo era substituído pelo selo e o resto do
   * código de barras se perdia — o pedido saía com um dígito só. Item que
   * chegou sem sugestão fica com o campo aberto até o fim; item sugerido pelo
   * EAN mostra o selo e abre o campo no "Trocar".
   */
  const mostrarCampo = editando || !item.sku_sugerido

  return (
    <tr className={erro ? 'bg-erro-suave/40' : 'bg-superficie'}>
      <td className={`${tdCls} tabular text-texto-suave`}>{item.numero_item}</td>

      <td className={tdCls}>
        <div className="text-[13.5px] font-medium text-tinta">{item.descricao}</div>
        <div className="tabular mt-0.5 text-xs text-texto-suave">
          Cód. fornecedor {item.codigo_fornecedor}
          {item.ean ? ` · EAN ${item.ean}` : ' · sem EAN no XML'}
          {item.ncm ? ` · NCM ${item.ncm}` : ''}
          {item.cfop ? ` · CFOP ${item.cfop}` : ''}
        </div>
      </td>

      <td className={`${tdCls} tabular text-right`}>{item.quantidade}</td>
      <td className={`${tdCls} tabular text-right`}>{formatarMoeda(item.valor_unitario_centavos)}</td>
      <td className={`${tdCls} tabular text-right font-semibold text-tinta`}>
        {formatarMoeda(item.valor_total_centavos)}
      </td>

      <td className={tdCls}>
        {!mostrarCampo && decisao.origem !== 'nenhuma' && (
          <div className="flex flex-wrap items-center gap-2">
            {decisao.origem === 'ean' && decisao.sku ? (
              <span className="inline-flex items-center gap-1.5 rounded-[6px] border border-borda bg-divisoria px-2 py-1 text-xs font-semibold text-texto">
                <Icone nome="check" tamanho={13} />
                {decisao.sku.produto} · {decisao.sku.cor} {decisao.sku.tamanho}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-[6px] border border-marca-barra bg-marca-suave px-2 py-1 text-xs font-semibold text-marca-forte">
                <Icone nome="check" tamanho={13} />
                <span className="tabular">{decisao.codigo}</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => aoEditar(true)}
              className="text-[12px] font-semibold text-marca underline-offset-2 hover:underline"
            >
              Trocar
            </button>
          </div>
        )}

        {mostrarCampo && (
          <div className="flex items-center gap-1.5">
            {/* O mesmo campo da ficha, pelo mesmo motivo: quem revisa a nota
                não tem a peça etiquetada na mão. Aqui escolher da lista só
                PREENCHE — a conciliação desta tela acontece na importação, não
                item a item. */}
            <BuscaDeSKU
              valor={valorDigitado}
              aoDigitar={aoEscolher}
              aoEscolher={(sku) => aoEscolher(sku.codigo_barras)}
              rotuloAcessivel={`Código de barras ou nome do SKU para o item ${item.numero_item}`}
              invalido={Boolean(erro)}
            />
            {editando && (
              <button
                type="button"
                onClick={() => {
                  aoEscolher('')
                  aoEditar(false)
                }}
                className="h-8 shrink-0 rounded-sistema px-2 text-[12px] font-semibold text-texto-suave hover:text-texto"
              >
                Voltar
              </button>
            )}
          </div>
        )}

        {erro ? (
          <span className="mt-1 block text-[11.5px] font-semibold text-erro">{erro}</span>
        ) : decisao.origem === 'nenhuma' ? (
          <span className="mt-1 block text-[11.5px] text-texto-suave">
            Sem SKU, este item não entra no estoque.
          </span>
        ) : (
          mostrarCampo && (
            /* O código só é conferido contra o catálogo na conclusão — dizer
               "conciliado" agora seria prometer o que ainda não foi checado. */
            <span className="mt-1 block text-[11.5px] text-texto-suave">
              Vai ser conciliado com este código ao importar.
            </span>
          )
        )}
      </td>
    </tr>
  )
}

/* ------------------------------------------------------------- utilidades */

/** Chave de 44 dígitos em blocos de 4 — a leitura em voz alta depende disso. */
function chaveEspacada(chave: string): string {
  return chave.replace(/(\d{4})(?=\d)/g, '$1 ')
}

function enderecoEmLinha(previa: PreviaNota): string {
  const e = previa.emitente.endereco
  const rua = [e.logradouro, e.numero].filter(Boolean).join(', ')
  const cidade = [e.cidade, e.uf].filter(Boolean).join(' — ')
  return [rua, e.bairro, cidade, e.cep ? `CEP ${mascararCEP(e.cep)}` : ''].filter(Boolean).join(' · ')
}

function mensagemDoResultado(
  numero: string,
  lancouEstoque: boolean,
  lancados: number,
  pendentes: number,
): string {
  const inicio = `Nota ${numero || 's/nº'} importada.`
  if (!lancouEstoque) {
    return `${inicio} O estoque não foi movimentado — lance pela ficha quando quiser.`
  }
  const entrada =
    lancados > 0
      ? ` ${lancados} ${lancados === 1 ? 'item entrou' : 'itens entraram'} no estoque.`
      : ' Nenhum item entrou no estoque.'
  const resto =
    pendentes > 0
      ? ` ${pendentes} ${pendentes === 1 ? 'item continua pendente' : 'itens continuam pendentes'} de conciliação.`
      : ''
  return inicio + entrada + resto
}
